/**
 * Path helpers for HQ's PAGE-VISIBILITY rules (`user-permissions` rows with
 * `isDenied`).
 *
 * These paths are WEB ROUTE PATHS (`layout + path`), not API service paths, and
 * nothing maps between the two. That is the whole reason page visibility is UI
 * gating and not a security boundary — see `stores/PageAccessStore.ts` and
 * "Page visibility is not a security boundary" in `packages/web/CLAUDE.md`.
 *
 * The type/function names keep the "permission" spelling because they are also
 * used by the library's grant-based `requiresPermission` route check, which is
 * a different (and genuinely permission-shaped) mechanism.
 */

/** One `user-permissions` row as the page-visibility checks read it. */
export interface PageAccessRule {
  id: number | string;
  userId: number | string;
  appName?: string;
  location?: string;
  isDenied?: boolean;
}

/**
 * The hide-rule predicates shared by PageAccessStore (own rules) and
 * PermissionsStore (the Page Visibility admin view): an explicit `isDenied` row
 * that applies to this app (legacy rows without an appName apply too),
 * optionally narrowed to one user.
 */
export function isDenyRuleForApp(
  rule: PageAccessRule | null | undefined,
  appName: string
): boolean {
  if (!rule || rule.isDenied !== true) return false;
  return !rule.appName || rule.appName === appName;
}

export function isActiveDenyRule(
  rule: PageAccessRule | null | undefined,
  userId: number | string,
  appName: string
): boolean {
  return (
    isDenyRuleForApp(rule, appName) &&
    String(rule?.userId) === String(userId)
  );
}

/**
 * The canonical permission path for a route: the explicit `permissionPath`
 * when set, else the layout-prefixed full path (e.g. "/app" + "/clients").
 *
 * CRITICAL: rules are AUTHORED against raw route definitions (the Page
 * Visibility view reads `routerStore.routesList`, where `path` is "/clients") but
 * ENFORCED against compiled Route instances (whose `permissionPath` is
 * `layout + path` = "/app/clients" — see makeRoute). Both sides MUST derive
 * the path through this one function or rules silently stop matching.
 */
export function routePermissionPath(route: {
  permissionPath?: string;
  layout?: string;
  path?: string;
}): string {
  if (route?.permissionPath) return route.permissionPath;
  return (route?.layout ?? "") + (route?.path ?? "");
}

/**
 * Shared permission-path matcher used by both the grant-based
 * `requiresPermission` route check and the deny-based page-visibility rules
 * (user_permissions rows with `isDenied`). One matcher keeps the two checks
 * from drifting apart.
 *
 * A rule location matches a page path when:
 * - it is an exact match (`/app/clients` matches `/app/clients`), or
 * - it is a wildcard (`/app/clients/*` matches `/app/clients` and anything
 *   under it — but not siblings like `/app/clients-archive`), or
 * - it is a parent path (`/app/clients` matches `/app/clients/detail`).
 */
export function matchesPermissionPath(
  ruleLocation: string,
  pagePath: string
): boolean {
  if (!ruleLocation || !pagePath) return false;

  if (ruleLocation === pagePath) return true;

  if (ruleLocation.endsWith("/*")) {
    const basePath = ruleLocation.slice(0, -2);
    return pagePath === basePath || pagePath.startsWith(basePath + "/");
  }

  return pagePath.startsWith(ruleLocation + "/");
}
